// MassDeleteToolbar — sticky bottom bar for admin-only mass-delete on list pages.
// Renders only when user.role === "admin" (or is_super_admin). Provides two
// flows:
//   1. Selected IDs  → POST /admin/bulk-delete
//   2. All filtered  → POST /admin/bulk-delete-all
//
// Each flow goes through the 2-step <MassDeleteConfirm> modal (typed phrase).
import { useState } from "react";
import { Trash2, ListX, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import MassDeleteConfirm from "./MassDeleteConfirm";

function isAdmin(user) {
  if (!user) return false;
  return (user.role || "").toLowerCase() === "admin" || user.is_super_admin;
}

export default function MassDeleteToolbar({
  user,                  // current user object
  collection,            // backend collection key (whitelisted)
  label,                 // human label, e.g. "Zeiteinträge"
  selectedIds = [],      // currently checked rows
  allFilteredCount = 0,  // total rows matching current filter
  allFilteredFilter = null, // optional Mongo-style filter object for "delete all filtered"
  onAfterDelete,         // callback after successful deletion
  onClearSelection,      // clears selected ids in the parent
}) {
  const [modal, setModal] = useState(null); // null | "selected" | "all"

  if (!isAdmin(user)) return null;
  if (selectedIds.length === 0 && allFilteredCount === 0) return null;

  async function doDeleteSelected() {
    try {
      const { data } = await api.post("/admin/bulk-delete", {
        collection,
        ids: selectedIds,
        confirm: "LOESCHEN",
      });
      toast.success(`${data.deleted} ${label} gelöscht`);
      setModal(null);
      onClearSelection?.();
      onAfterDelete?.(data.deleted);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschung fehlgeschlagen");
    }
  }

  async function doDeleteAll() {
    try {
      const { data } = await api.post("/admin/bulk-delete-all", {
        collection,
        filter: allFilteredFilter || {},
        expected_count: allFilteredCount,
        confirm: "LOESCHEN",
      });
      toast.success(`${data.deleted} ${label} gelöscht`);
      setModal(null);
      onClearSelection?.();
      onAfterDelete?.(data.deleted);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschung fehlgeschlagen");
    }
  }

  return (
    <>
      <div
        data-testid="mass-delete-toolbar"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-zinc-900 border border-rose-500/40 rounded-lg shadow-2xl px-4 py-2.5 flex items-center gap-3 max-w-2xl"
      >
        <div className="text-xs text-zinc-300">
          <span className="text-rose-300 font-semibold">Admin</span>
          {selectedIds.length > 0 && (
            <>
              <span className="text-zinc-600 mx-2">·</span>
              <span className="font-semibold text-zinc-100">{selectedIds.length}</span> ausgewählt
            </>
          )}
          {selectedIds.length === 0 && allFilteredCount > 0 && (
            <>
              <span className="text-zinc-600 mx-2">·</span>
              <span className="font-semibold text-zinc-100">{allFilteredCount}</span> gefiltert
            </>
          )}
        </div>

        {selectedIds.length > 0 && (
          <button
            onClick={() => setModal("selected")}
            data-testid="mass-delete-selected-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-rose-600 hover:bg-rose-700 text-white rounded"
          >
            <Trash2 className="w-3.5 h-3.5" /> Auswahl löschen
          </button>
        )}

        {allFilteredCount > 0 && (
          <button
            onClick={() => setModal("all")}
            data-testid="mass-delete-all-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded"
            title={`Alle ${allFilteredCount} gefilterten löschen`}
          >
            <ListX className="w-3.5 h-3.5" /> Alle gefilterten ({allFilteredCount})
          </button>
        )}

        {selectedIds.length > 0 && (
          <button
            onClick={onClearSelection}
            data-testid="mass-delete-clear-btn"
            className="p-1 text-zinc-500 hover:text-zinc-200"
            title="Auswahl aufheben"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <MassDeleteConfirm
        open={modal === "selected"}
        count={selectedIds.length}
        label={`${selectedIds.length} ${label} löschen`}
        onCancel={() => setModal(null)}
        onConfirm={doDeleteSelected}
      />
      <MassDeleteConfirm
        open={modal === "all"}
        count={allFilteredCount}
        label={`Alle ${allFilteredCount} gefilterten ${label} löschen`}
        onCancel={() => setModal(null)}
        onConfirm={doDeleteAll}
      />
    </>
  );
}
